import { ErrorMessage, Input } from '@/components';
import {
  NUMBER_INPUT_MAX_VALUE,
  handleInvalidNumber,
  isValidNumberFormatValues,
} from '@/utils';
import Big from 'big.js';
import type { CSSProperties, ReactNode } from 'react';
import {
  Controller,
  type FieldErrors,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { Form } from '.';
import { FieldLabel, type FieldLabelSize } from './FieldLabel';
import type { FormControlProp } from './types';

type Props<T extends FieldValues> = {
  name: Path<T>;
  label?: string;
  control: FormControlProp<T>;
  errors: FieldErrors;
  required?: boolean;
  disabled?: boolean;
  addonAfter?: ReactNode;
  placeholder?: string;
  disableSeparator?: boolean;
  decimalScale?: number;
  allowNegative?: boolean;
  maxLength?: number;
  max?: number;
  min?: number;
  marginBottom?: number;
  hint?: string;
  shouldCoexistOnChange?: boolean;
  onChange?: (values: number | undefined) => void;
  width?: CSSProperties['width'];
  labelSize?: FieldLabelSize;
};

export const NumericFormatField = <T extends FieldValues>({
  name,
  label,
  control,
  errors,
  required,
  disabled = false,
  addonAfter,
  placeholder,
  disableSeparator,
  decimalScale = 0,
  allowNegative = true,
  maxLength,
  max = NUMBER_INPUT_MAX_VALUE,
  min = NUMBER_INPUT_MAX_VALUE * -1,
  marginBottom,
  hint,
  shouldCoexistOnChange = false,
  onChange,
  width = '100%',
  labelSize = 'medium',
}: Props<T>) => {
  return (
    <Form.Item
      style={{ marginBottom, width }}
      label={
        label && (
          <FieldLabel
            text={label}
            required={required}
            hint={hint}
            size={labelSize}
          />
        )
      }
    >
      <Controller
        control={control}
        name={name}
        render={({
          field: { value, ref, onChange: fieldOnChange, ...field },
        }) => {
          return (
            <NumericFormat
              {...field}
              customInput={Input}
              getInputRef={ref}
              thousandSeparator={!disableSeparator}
              placeholder={placeholder}
              addonAfter={addonAfter}
              maxLength={maxLength}
              allowNegative={allowNegative}
              decimalScale={decimalScale}
              value={value ?? ''}
              disabled={disabled}
              onBlur={({ target: { value } }) => {
                handleInvalidNumber(value, fieldOnChange);
              }}
              onKeyDown={({ key, currentTarget: { value } }) => {
                if (key === 'Enter') {
                  handleInvalidNumber(value, fieldOnChange);
                }
              }}
              onValueChange={({ floatValue, value }) => {
                if (!isValidNumberFormatValues({ floatValue, value })) {
                  return;
                }

                if (shouldCoexistOnChange || !onChange) {
                  fieldOnChange(floatValue ?? null);
                }
                onChange?.(floatValue);
              }}
              isAllowed={({ floatValue }) => {
                if (floatValue === undefined || Number.isNaN(floatValue)) {
                  return true;
                }

                const compareValue = new Big(floatValue);
                return compareValue.gte(min) && compareValue.lte(max);
              }}
            />
          );
        }}
      />
      <ErrorMessage errors={errors} name={name} />
    </Form.Item>
  );
};
